import { useRef, useState } from 'react';
import { postApiV1ExamAnswerAiCorrectionBatch } from '@/shared/api/generated';
import type { ExamItemAnswerAiCorrectionViewModel } from '@/shared/api/generated/types';

/**
 * Quantidade máxima de `answerId` enviada por requisição ao endpoint de correção
 * em lote — um lote com muitas dezenas de respostas numa única chamada corre risco
 * de estourar o timeout padrão do Axios, então o array é fatiado em pedaços desse
 * tamanho e enviado em requisições sequenciais.
 */
export const AI_BATCH_CHUNK_SIZE = 20;

export interface UseBatchAiCorrectionCallbacks {
  /** Chamado com o array combinado de todos os pedaços, após todos terem sucesso. */
  readonly onSuccess?: (results: ExamItemAnswerAiCorrectionViewModel[]) => void;
  /** Chamado se qualquer pedaço falhar — nenhum resultado (nem de pedaços já bem-sucedidos) é entregue. */
  readonly onError?: (error: unknown) => void;
}

/** Progresso do envio em pedaços — usado para exibir uma barra de progresso na UI. */
export interface BatchAiCorrectionProgress {
  /** Quantidade de `answerId` já enviada (pedaços concluídos). */
  readonly completed: number;
  /** Total de `answerId` a enviar. */
  readonly total: number;
}

/**
 * Hook para solicitar a correção automática por IA de várias respostas dissertativas,
 * fatiando o envio em pedaços de {@link AI_BATCH_CHUNK_SIZE} para não estourar timeout.
 *
 * Retorna sugestão de nota e feedback apenas para as respostas efetivamente
 * corrigidas — respostas inelegíveis são puladas em silêncio pelo backend e não
 * aparecem no array de resposta. Nenhuma nota é salva no banco.
 *
 * As requisições são sequenciais e tratadas como uma única operação atômica: se
 * qualquer pedaço falhar, os resultados dos pedaços já concluídos são descartados e
 * `onError` é chamado — nenhum estado de sugestão deve ser alterado com um resultado
 * parcial.
 *
 * @returns `mutate` (mesma assinatura de uma mutation do TanStack Query), `isPending`,
 * `progress` (`{ completed, total }` dos `answerId` já enviados, `null` fora de uma
 * correção em andamento) — para exibir uma `ProgressBar` em vez de um spinner indefinido
 * — e `cancel`, para o operador desistir e descartar tudo (ver abaixo)
 *
 * `cancel` também aborta a requisição HTTP do pedaço em voo (via `AbortController`) —
 * a chamada ao endpoint em lote é feita direto pelo cliente gerado pelo Kubb, não
 * pelo hook de mutation (que não expõe um `signal` por chamada), justamente para
 * poder plugar o `signal` nela. Além de abortar a requisição, `cancel` impede que o
 * loop envie o próximo pedaço e que `onSuccess`/`onError` sejam chamados quando a
 * promise resolver — mesmo os pedaços já concluídos são descartados, coerente com o
 * aviso de "todo o progresso será perdido" exibido antes de cancelar.
 *
 * @example
 * ```tsx
 * const { mutate: correctBatch, isPending, progress, cancel } = useBatchAiCorrection();
 * correctBatch({ data: [101, 102, 103] }, { onSuccess: results => console.log(results.length) });
 * // ao desistir no meio do caminho:
 * cancel();
 * ```
 */
export function useBatchAiCorrection() {
  const [isPending, setIsPending] = useState(false);
  const [progress, setProgress] = useState<BatchAiCorrectionProgress | null>(null);
  const cancelledRef = useRef(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  function mutate(
    { data: answerIds }: { data: readonly number[] },
    callbacks?: UseBatchAiCorrectionCallbacks,
  ) {
    cancelledRef.current = false;
    const abortController = new AbortController();
    abortControllerRef.current = abortController;
    setIsPending(true);
    setProgress({ completed: 0, total: answerIds.length });

    (async () => {
      const results: ExamItemAnswerAiCorrectionViewModel[] = [];
      for (let start = 0; start < answerIds.length; start += AI_BATCH_CHUNK_SIZE) {
        if (cancelledRef.current) break;
        const chunk = answerIds.slice(start, start + AI_BATCH_CHUNK_SIZE);
        const chunkResults = await postApiV1ExamAnswerAiCorrectionBatch(chunk, {
          signal: abortController.signal,
        });
        results.push(...chunkResults);
        setProgress({ completed: start + chunk.length, total: answerIds.length });
      }
      return results;
    })()
      .then(results => {
        if (!cancelledRef.current) callbacks?.onSuccess?.(results);
      })
      .catch((error: unknown) => {
        if (!cancelledRef.current) callbacks?.onError?.(error);
      })
      .finally(() => {
        setIsPending(false);
        setProgress(null);
      });
  }

  /**
   * Desiste da correção em andamento: aborta a requisição do pedaço em voo, impede o
   * envio de pedaços seguintes e descarta o resultado (mesmo o já obtido) — nenhum
   * `onSuccess`/`onError` é chamado.
   */
  function cancel() {
    cancelledRef.current = true;
    abortControllerRef.current?.abort();
  }

  return { mutate, isPending, progress, cancel } as const;
}
